import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useAlertSync } from '../hooks/useAlertSync';
import { useModalManager } from '../hooks/useModalManager';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { AlertForm } from '../components/alerts/AlertForm';
import { DataState } from '../components/ui/DataState';
import { useCoinsList } from '../hooks/queries/useCoinGecko';
import { useAuthStore } from '../store/authStore';
import type { PriceAlert } from '../services/alertService';
import type { Token } from '../types';
import './AlertsPage.css';

interface AlertsPageProps {
  onNavigate?: (pageId: string) => void;
}

type FilterTab = 'all' | 'active' | 'triggered' | 'paused';

export function AlertsPage({ onNavigate }: AlertsPageProps) {
  const { t } = useTranslation();
  const { user, isAuthenticated } = useAuthStore();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all');
  const modals = useModalManager<'createForm'>();
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredAlertId, setHoveredAlertId] = useState<string | null>(null);
  const { confirm, confirmationProps } = useConfirmation();

  const {
    alerts,
    isLoading,
    isSyncing,
    syncError,
    lastSyncedAt,
    deleteAlert,
    toggleAlert,
    clearTriggeredAlerts,
    refreshAlerts,
  } = useAlertSync();

  // Fetch all coins for AlertForm
  const { data: allCoins = [] } = useCoinsList({ enabled: modals.isOpen('createForm') });

  // Convert coins to tokens format for AlertForm
  const tokensForForm: Token[] = useMemo(() => {
    return allCoins.map((coin: any) => ({
      id: coin.id,
      coin_id: coin.id,
      symbol: coin.symbol,
      name: coin.name,
      displayName: coin.symbol?.toUpperCase() || coin.name,
      positions: [],
    }));
  }, [allCoins]);

  // Build current prices from alerts' token data
  const currentPrices = useMemo(() => {
    const prices: Record<string, { usd: number }> = {};
    // This would ideally come from a price hook, but for now we'll use placeholder
    return prices;
  }, []);

  // Refresh alerts on mount
  useEffect(() => {
    refreshAlerts();
    const interval = setInterval(refreshAlerts, 30000);
    return () => clearInterval(interval);
  }, [refreshAlerts]);

  // Filter alerts based on active tab and search
  const filteredAlerts = useMemo(() => {
    let filtered = alerts;

    // Filter by tab
    if (activeFilter === 'active') {
      filtered = filtered.filter(a => a.enabled && !a.triggeredAt);
    } else if (activeFilter === 'triggered') {
      filtered = filtered.filter(a => a.triggeredAt);
    } else if (activeFilter === 'paused') {
      filtered = filtered.filter(a => !a.enabled && !a.triggeredAt);
    }

    // Filter by search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(a =>
        a.tokenName.toLowerCase().includes(query) ||
        a.tokenId.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [alerts, activeFilter, searchQuery]);

  // Stats
  const stats = useMemo(() => ({
    total: alerts.length,
    active: alerts.filter(a => a.enabled && !a.triggeredAt).length,
    triggered: alerts.filter(a => a.triggeredAt).length,
    paused: alerts.filter(a => !a.enabled && !a.triggeredAt).length,
  }), [alerts]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    if (minutes < 1) return t('alerts.justNow', 'À l\'instant');
    if (minutes < 60) return t('alerts.minutesAgo', { count: minutes }, `Il y a ${minutes}min`);
    if (hours < 24) return t('alerts.hoursAgo', { count: hours }, `Il y a ${hours}h`);
    if (days < 7) return t('alerts.daysAgo', { count: days }, `Il y a ${days}j`);
    return date.toLocaleDateString();
  };

  const getConditionText = (alert: PriceAlert) => {
    if (alert.condition === 'above' && alert.targetPrice) {
      return `${t('alerts.abovePrice', 'Au-dessus de')} $${alert.targetPrice.toLocaleString()}`;
    }
    if (alert.condition === 'below' && alert.targetPrice) {
      return `${t('alerts.belowPrice', 'En-dessous de')} $${alert.targetPrice.toLocaleString()}`;
    }
    if (alert.condition === 'change_percent' && alert.changePercent) {
      const sign = alert.changePercent > 0 ? '+' : '';
      return `${t('alerts.percentChange', 'Variation')} ${sign}${alert.changePercent}%`;
    }
    return t('alerts.unknownCondition', 'Condition inconnue');
  };

  const getConditionIcon = (alert: PriceAlert) => {
    if (alert.condition === 'above') return <Icons.TrendingUp size={16} />;
    if (alert.condition === 'below') return <Icons.TrendingDown size={16} />;
    return <Icons.Activity size={16} />;
  };

  const handleDeleteAlert = async (id: string) => {
    const confirmed = await confirm({
      title: t('alerts.deleteAlertTitle', 'Supprimer cette alerte ?'),
      message: t('alerts.deleteAlertMessage', 'Cette action est irréversible.'),
      variant: 'danger',
      confirmText: t('common.delete', 'Supprimer'),
      cancelText: t('common.cancel', 'Annuler'),
    });
    if (confirmed) {
      await deleteAlert(id);
    }
  };

  const handleToggleAlert = async (id: string) => {
    await toggleAlert(id);
  };

  const handleClearTriggered = async () => {
    const confirmed = await confirm({
      title: t('alerts.clearTriggeredTitle', 'Supprimer toutes les alertes déclenchées ?'),
      message: t('alerts.clearTriggeredMessage', 'Cette action est irréversible.'),
      variant: 'danger',
      confirmText: t('common.delete', 'Supprimer'),
      cancelText: t('common.cancel', 'Annuler'),
    });
    if (confirmed) {
      await clearTriggeredAlerts();
    }
  };

  // Not authenticated - show login prompt
  if (!isAuthenticated) {
    return (
      <div className="alerts-page">
        <div className="alerts-page__auth-required">
          <div className="alerts-page__auth-icon">
            <Icons.Lock size={48} />
          </div>
          <h2>{t('alerts.authRequired', 'Connexion requise')}</h2>
          <p>{t('alerts.authRequiredDesc', 'Connectez-vous pour gérer vos alertes de prix.')}</p>
          <button className="alerts-page__auth-btn" onClick={() => onNavigate?.('settings')}>
            <Icons.LogIn size={18} />
            {t('common.login', 'Se connecter')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="alerts-page">
      {/* Header */}
      <header className="alerts-page__header">
        <div className="alerts-page__header-left">
          <h1>
            <Icons.Bell size={28} />
            {t('alerts.priceAlerts', 'Alertes Prix')}
          </h1>
          <p>{t('alerts.pageDescription', 'Gérez vos alertes et recevez des notifications quand vos cibles sont atteintes.')}</p>
        </div>
        <div className="alerts-page__header-actions">
          {isSyncing ? (
            <span className="alerts-page__sync-status alerts-page__sync-status--syncing">
              <Icons.RefreshCw size={14} className="animate-spin" />
              {t('common.syncing', 'Synchronisation...')}
            </span>
          ) : syncError ? (
            <span className="alerts-page__sync-status alerts-page__sync-status--error" title={syncError}>
              <Icons.AlertCircle size={14} />
              {t('common.error', 'Erreur')}
            </span>
          ) : lastSyncedAt ? (
            <span className="alerts-page__sync-status alerts-page__sync-status--success">
              <Icons.Check size={14} />
              {t('common.synced', 'Synchronisé')}
            </span>
          ) : null}
          <button className="alerts-page__create-btn" onClick={() => modals.open('createForm')}>
            <Icons.Plus size={18} />
            {t('alerts.newAlert', 'Nouvelle alerte')}
          </button>
        </div>
      </header>

      {/* KPI Cards */}
      <div className="alerts-page__kpi-row">
        <div className="alerts-page__kpi-card alerts-page__kpi-card--total">
          <div className="alerts-page__kpi-watermark">
            <Icons.Bell size={48} />
          </div>
          <div className="alerts-page__kpi-content">
            <span className="alerts-page__kpi-label">{t('alerts.totalAlerts', 'Total Alertes')}</span>
            <span className="alerts-page__kpi-value">{stats.total}</span>
          </div>
        </div>

        <div className="alerts-page__kpi-card alerts-page__kpi-card--active">
          <div className="alerts-page__kpi-watermark">
            <Icons.Activity size={48} />
          </div>
          <div className="alerts-page__kpi-content">
            <span className="alerts-page__kpi-label">{t('alerts.active', 'Actives')}</span>
            <span className="alerts-page__kpi-value">{stats.active}</span>
          </div>
        </div>

        <div className="alerts-page__kpi-card alerts-page__kpi-card--triggered">
          <div className="alerts-page__kpi-watermark">
            <Icons.CheckCircle size={48} />
          </div>
          <div className="alerts-page__kpi-content">
            <span className="alerts-page__kpi-label">{t('alerts.triggered', 'Déclenchées')}</span>
            <span className="alerts-page__kpi-value">{stats.triggered}</span>
          </div>
        </div>

        <div className="alerts-page__kpi-card alerts-page__kpi-card--paused">
          <div className="alerts-page__kpi-watermark">
            <Icons.PauseCircle size={48} />
          </div>
          <div className="alerts-page__kpi-content">
            <span className="alerts-page__kpi-label">{t('alerts.inactive', 'En pause')}</span>
            <span className="alerts-page__kpi-value">{stats.paused}</span>
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="alerts-page__filters">
        <div className="alerts-page__tabs">
          {(['all', 'active', 'triggered', 'paused'] as FilterTab[]).map(tab => (
            <button
              key={tab}
              className={`alerts-page__tab ${activeFilter === tab ? 'alerts-page__tab--active' : ''}`}
              onClick={() => setActiveFilter(tab)}
            >
              {tab === 'all' && <Icons.List size={16} />}
              {tab === 'active' && <Icons.Activity size={16} />}
              {tab === 'triggered' && <Icons.CheckCircle size={16} />}
              {tab === 'paused' && <Icons.PauseCircle size={16} />}
              <span>
                {tab === 'all' && t('alerts.all', 'Toutes')}
                {tab === 'active' && t('alerts.active', 'Actives')}
                {tab === 'triggered' && t('alerts.triggered', 'Déclenchées')}
                {tab === 'paused' && t('alerts.inactive', 'En pause')}
              </span>
              {tab === 'active' && stats.active > 0 && (
                <span className="alerts-page__tab-badge">{stats.active}</span>
              )}
              {tab === 'triggered' && stats.triggered > 0 && (
                <span className="alerts-page__tab-badge alerts-page__tab-badge--triggered">{stats.triggered}</span>
              )}
            </button>
          ))}
        </div>

        <div className="alerts-page__search">
          <Icons.Search size={16} />
          <input
            type="text"
            placeholder={t('alerts.searchPlaceholder', 'Rechercher un token...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button onClick={() => setSearchQuery('')} className="alerts-page__search-clear">
              <Icons.X size={14} />
            </button>
          )}
        </div>

        {activeFilter === 'triggered' && stats.triggered > 0 && (
          <button className="alerts-page__clear-btn" onClick={handleClearTriggered}>
            <Icons.Trash2 size={14} />
            {t('alerts.clearTriggered', 'Effacer')}
          </button>
        )}
      </div>

      {/* Alerts Grid — V2.I.6 : DataState wrapper unifies loading / error /
          empty / data states with the rest of the app. */}
      <div className="alerts-page__content">
        <DataState
          loading={isLoading}
          error={syncError}
          onRetry={refreshAlerts}
          retryLabel={t('common.retry', 'Réessayer')}
          isEmpty={filteredAlerts.length === 0}
          headingLevel="h2"
          loadingIcon={<Icons.RefreshCw size={48} className="animate-spin" />}
          loadingMessage={t('alerts.loading', 'Chargement des alertes...')}
          errorIcon={<Icons.AlertCircle size={48} />}
          errorTitle={t('alerts.syncErrorTitle', 'Synchronisation impossible')}
          emptyIcon={<Icons.Bell size={48} />}
          emptyTitle={
            activeFilter === 'all'
              ? t('alerts.noAlertsCreated', 'Aucune alerte créée')
              : activeFilter === 'active'
                ? t('alerts.noActiveAlerts', 'Aucune alerte active')
                : activeFilter === 'triggered'
                  ? t('alerts.noTriggeredAlerts', 'Aucune alerte déclenchée')
                  : t('alerts.noPausedAlerts', 'Aucune alerte en pause')
          }
          emptyMessage={t(
            'alerts.noAlertsDesc',
            "Créez des alertes pour être notifié lorsque le prix d'un token atteint votre cible",
          )}
          emptyAction={
            activeFilter !== 'triggered' ? (
              <button className="alerts-page__empty-btn" onClick={() => modals.open('createForm')}>
                <Icons.Plus size={18} />
                {t('alerts.createAlert', 'Créer une alerte')}
              </button>
            ) : undefined
          }
        >
          <div className="alerts-page__grid">
            {filteredAlerts.map(alert => {
              const isTriggered = !!alert.triggeredAt;
              const isActive = alert.enabled && !isTriggered;
              const isPaused = !alert.enabled && !isTriggered;
              const isHovered = hoveredAlertId === alert.id;

              return (
                <div
                  key={alert.id}
                  className={`alerts-page__alert-card ${isTriggered ? 'alerts-page__alert-card--triggered' : ''} ${isPaused ? 'alerts-page__alert-card--paused' : ''} ${isHovered ? 'alerts-page__alert-card--hover' : ''}`}
                  onMouseEnter={() => setHoveredAlertId(alert.id)}
                  onMouseLeave={() => setHoveredAlertId(null)}
                >
                  {/* Card Header */}
                  <div className="alerts-page__alert-header">
                    <div className="alerts-page__alert-token">
                      <div className={`alerts-page__alert-icon ${isTriggered ? 'alerts-page__alert-icon--triggered' : isActive ? 'alerts-page__alert-icon--active' : ''}`}>
                        {getConditionIcon(alert)}
                      </div>
                      <div className="alerts-page__alert-token-info">
                        <span className="alerts-page__alert-token-name">{alert.tokenName}</span>
                        <span className="alerts-page__alert-token-id">{alert.tokenId}</span>
                      </div>
                    </div>
                    <div className="alerts-page__alert-actions">
                      {!isTriggered && (
                        <button
                          className="alerts-page__alert-action"
                          onClick={() => handleToggleAlert(alert.id)}
                          title={alert.enabled ? t('alerts.deactivate', 'Désactiver') : t('alerts.activate', 'Activer')}
                        >
                          {alert.enabled ? <Icons.PauseCircle size={18} /> : <Icons.PlayCircle size={18} />}
                        </button>
                      )}
                      <button
                        className="alerts-page__alert-action alerts-page__alert-action--delete"
                        onClick={() => handleDeleteAlert(alert.id)}
                        title={t('alerts.delete', 'Supprimer')}
                      >
                        <Icons.Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="alerts-page__alert-body">
                    <div className="alerts-page__alert-condition">
                      <span className="alerts-page__alert-condition-label">{t('alerts.condition', 'Condition')}</span>
                      <span className="alerts-page__alert-condition-value">{getConditionText(alert)}</span>
                    </div>

                    {alert.repeatable && (
                      <div className="alerts-page__alert-badge alerts-page__alert-badge--repeatable">
                        <Icons.RefreshCw size={12} />
                        {t('alerts.repeatable', 'Répétable')}
                      </div>
                    )}

                    {isTriggered && alert.triggeredAt && (
                      <div className="alerts-page__alert-triggered-info">
                        <Icons.CheckCircle size={14} />
                        <span>{t('alerts.triggeredAt', 'Déclenchée')} {formatDate(alert.triggeredAt)}</span>
                      </div>
                    )}
                  </div>

                  {/* Card Footer */}
                  <div className="alerts-page__alert-footer">
                    <span className="alerts-page__alert-date">
                      <Icons.Clock size={12} />
                      {t('alerts.createdAt', 'Créée')} {formatDate(alert.createdAt)}
                    </span>
                    <span className={`alerts-page__alert-status ${isTriggered ? 'alerts-page__alert-status--triggered' : isActive ? 'alerts-page__alert-status--active' : 'alerts-page__alert-status--paused'}`}>
                      {isTriggered ? (
                        <><Icons.CheckCircle size={12} /> {t('alerts.triggered', 'Déclenchée')}</>
                      ) : isActive ? (
                        <><Icons.Activity size={12} /> {t('alerts.active', 'Active')}</>
                      ) : (
                        <><Icons.PauseCircle size={12} /> {t('alerts.inactive', 'En pause')}</>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </DataState>
      </div>

      {/* Create Alert Form Modal */}
      {modals.isOpen('createForm') && (
        <AlertForm
          isOpen={modals.isOpen('createForm')}
          onClose={() => modals.close('createForm')}
          onSuccess={() => {
            modals.close('createForm');
            refreshAlerts();
            setActiveFilter('active');
          }}
          tokens={tokensForForm}
          currentPrices={currentPrices}
        />
      )}

      {/* V2.I.5.1 : confirmation modal replaces native confirm() */}
      <ConfirmationModal {...confirmationProps} />
    </div>
  );
}

export default AlertsPage;
